import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { usePage } from "@/components/page/PageContext";
import { InviteMemberButton } from "@/components/playlist/InviteMemberButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useDataTable } from "@/hooks/use-data-table";
import { getTeamMembers, removeTeamMember } from "@/queries/team";
import { useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Calendar, Trash2, User, UserCheck, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

interface TeamMember {
  id: number;
  user: {
    id: number;
    email: string;
    name: string;
  };
  team_id: number;
  team_name: string;
  role: string;
  created_at: string;
}

export default function MembersPage() {
  const { setTitles } = usePage();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    setTitles(["Members"]);
  }, []);

  const { data: members = [], isLoading, error } = getTeamMembers();

  const columns = useMemo<ColumnDef<TeamMember>[]>(
    () => [
      {
        id: "created_at",
        accessorKey: "created_at",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Member Since" />
        ),
        cell: ({ getValue }) => {
          const date = new Date(getValue() as string);
          return <span className="text-sm">{date.toLocaleDateString()}</span>;
        },
        meta: {
          label: "Member Since",
          icon: Calendar,
        },
        enableSorting: true,
      },
      {
        id: "user",
        accessorKey: "user.name",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="User" />
        ),
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium text-sm">
              {row.original.user.name}
            </span>
            <span className="text-xs text-gray-500">
              {row.original.user.email}
            </span>
          </div>
        ),
        meta: {
          label: "User",
          icon: User,
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          return rowA.original.user.name.localeCompare(rowB.original.user.name);
        },
      },
      {
        id: "team_name",
        accessorKey: "team_name",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Team" />
        ),
        cell: ({ getValue }) => (
          <span className="font-medium">{getValue() as string}</span>
        ),
        meta: {
          label: "Team Name",
          icon: Users,
        },
        enableSorting: true,
      },
      {
        id: "role",
        accessorKey: "role",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Role" />
        ),
        cell: ({ getValue }) => {
          const role = getValue() as string;

          return (
            <Badge variant={"secondary"} className={`uppercase font-medium`}>
              {role}
            </Badge>
          );
        },
        meta: {
          label: "Role",
          icon: UserCheck,
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const aRole = rowA.original.role.toLowerCase();
          const bRole = rowB.original.role.toLowerCase();
          // Leaders first, then members
          if (aRole === "leader" && bRole !== "leader") return -1;
          if (aRole !== "leader" && bRole === "leader") return 1;
          return aRole.localeCompare(bRole);
        },
      },
      {
        id: "status",
        accessorKey: "status",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: ({ getValue }) => {
          const status = getValue() as string;
          return (
            <Badge variant={"secondary"} className={`uppercase font-medium`}>
              {status}
            </Badge>
          );
        },
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const member = row.original;
          return <RemoveMemberButton member={member} />;
        },
        enableSorting: false,
      },
    ],
    []
  );

  const { table } = useDataTable({
    data: members,
    columns,
    pageCount: 1,
    initialState: {
      sorting: [{ id: "created_at", desc: true }], // Sort by member since date, newest first
    },
  });

  if (isLoading) {
    return (
      <div className="container mx-auto p-3 md:p-6">
        <h1 className="text-2xl font-bold mb-4">Members</h1>
        <div className="flex items-center justify-center h-32">
          <span className="text-gray-500">Loading members...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-3 md:p-6">
        <h1 className="text-2xl font-bold mb-4">Members</h1>
        <div className="flex items-center justify-center h-32">
          <span className="text-red-500">Error loading members</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <DataTable table={table} hidePagination cellClassName="py-3">
        <DataTableToolbar table={table}>
          <InviteMemberButton />
        </DataTableToolbar>
      </DataTable>
    </div>
  );
}

function RemoveMemberButton({ member }: { member: TeamMember }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const removeTeamMemberMutation = removeTeamMember();

  const handleRemoveMember = async () => {
    try {
      await removeTeamMemberMutation.mutateAsync({
        teamId: member.team_id,
        memberId: member.id,
      });

      // Invalidate and refetch team members
      queryClient.invalidateQueries({ queryKey: ["team_members"] });
      toast({
        title: "Success",
        description: `${member.user.name} has been removed from the team`,
      });
      setIsOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to remove team member",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          disabled={removeTeamMemberMutation.isPending}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove Team Member</DialogTitle>
          <DialogDescription>
            Are you sure you want to remove <strong>{member.user.name}</strong>{" "}
            from the team <strong>{member.team_name}</strong>? This action
            cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleRemoveMember}
            disabled={removeTeamMemberMutation.isPending}
          >
            {removeTeamMemberMutation.isPending
              ? "Removing..."
              : "Remove Member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
